import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneOff, Mic, MicOff, Grid3x3, X, AlertCircle, Loader2 } from 'lucide-react';
import { useBrowserCall, leadDisplayName } from '../contexts/BrowserCallContext';
import { formatPhone } from '../utils/formatters';

// In-call widget for browser calling. Lives in Layout so it survives
// navigation; bottom sheet on mobile, compact card bottom-right on
// desktop, stacked above the Ask Rhino FAB so neither covers the other.

function useTicker(active) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
}

function elapsed(startedAt, endedAt) {
  if (!startedAt) return '0:00';
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const s = Math.max(0, Math.floor((end - new Date(startedAt).getTime()) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

export default function BrowserCallDock() {
  const { state, busy, hangUp, toggleMute, sendDigits, dismiss } = useBrowserCall();
  const [keypad, setKeypad] = useState(false);
  useTicker(state.status === 'active');
  useEffect(() => { if (state.status !== 'active') setKeypad(false); }, [state.status]);

  const visible = state.status !== 'idle';
  const name = leadDisplayName(state.lead);
  const title = {
    connecting: 'Calling…',
    ringing: 'Ringing…',
    active: elapsed(state.startedAt, null),
    ended: state.startedAt ? `Call ended · ${elapsed(state.startedAt, state.endedAt)}` : 'Call ended',
    error: 'Call failed',
  }[state.status] || '';

  const isIos = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="browser-call-dock"
          role="dialog"
          aria-label="Call in progress"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.18 }}
          className="fixed z-50 inset-x-0 bottom-0 md:inset-x-auto md:right-5 md:bottom-24 md:w-[320px] bg-white border-t md:border border-gray-200 md:rounded-2xl shadow-2xl overflow-hidden"
        >
          <div className={`px-4 py-3 flex items-center gap-3 ${
            state.status === 'error' ? 'bg-red-50' : state.status === 'ended' ? 'bg-gray-50' : 'bg-teal-600 text-white'
          }`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
              state.status === 'error' ? 'bg-red-100 text-red-600' : state.status === 'ended' ? 'bg-gray-200 text-gray-600' : 'bg-white/20'
            }`}>
              {state.status === 'connecting' ? <Loader2 size={18} className="animate-spin" />
                : state.status === 'error' ? <AlertCircle size={18} />
                : state.status === 'ended' ? <PhoneOff size={18} />
                : <Phone size={18} className={state.status === 'ringing' ? 'animate-pulse' : ''} />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">
                {state.lead?._id ? (
                  <Link to={`/leads/${state.lead._id}`} className="hover:underline">{name}</Link>
                ) : name}
              </div>
              <div className={`text-xs truncate ${state.status === 'error' || state.status === 'ended' ? 'text-gray-500' : 'text-white/80'}`}>
                {title} · {formatPhone(state.phone)}
              </div>
            </div>
            {!busy && (
              <button type="button" onClick={dismiss} aria-label="Close"
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                <X size={16} />
              </button>
            )}
          </div>

          {state.status === 'error' && (
            <div className="px-4 py-3 text-sm text-red-700">
              {state.error}
              {state.phone && (
                <div className="mt-2">
                  <a href={`tel:${state.phone}`} className="text-teal-700 font-medium hover:underline">Call with your desk phone instead</a>
                </div>
              )}
            </div>
          )}

          {busy && (
            <div className="px-4 py-3">
              {keypad && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {KEYS.map((k) => (
                    <button key={k} type="button" onClick={() => sendDigits(k)}
                      className="h-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-base font-semibold text-gray-800">
                      {k}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <button type="button" onClick={toggleMute} disabled={state.status !== 'active'}
                  className={`flex-1 h-11 rounded-lg inline-flex items-center justify-center gap-1.5 text-sm font-medium disabled:opacity-40 ${
                    state.muted ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}>
                  {state.muted ? <MicOff size={16} /> : <Mic size={16} />}
                  {state.muted ? 'Unmute' : 'Mute'}
                </button>
                <button type="button" onClick={() => setKeypad((v) => !v)} disabled={state.status !== 'active'}
                  className={`flex-1 h-11 rounded-lg inline-flex items-center justify-center gap-1.5 text-sm font-medium disabled:opacity-40 ${
                    keypad ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}>
                  <Grid3x3 size={16} /> Keypad
                </button>
                <button type="button" onClick={hangUp}
                  className="flex-1 h-11 rounded-lg inline-flex items-center justify-center gap-1.5 text-sm font-semibold bg-red-600 text-white hover:bg-red-700">
                  <PhoneOff size={16} /> {state.status === 'active' ? 'Hang up' : 'Cancel'}
                </button>
              </div>
              {isIos && (
                <p className="mt-2 text-[11px] text-gray-500">Keep this tab open — switching apps on iPhone pauses the audio.</p>
              )}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
